/**
 * Null-safe display names for people (contacts and sales/users).
 *
 * Names come from forms, CSV/JSON imports and the inbound-email webhook, so a
 * part can be null, undefined, empty, only whitespace or even the literal
 * string "null" (a stringified missing value from an import). All of those
 * count as "missing": a contact without last name renders "Pablo", never
 * "Pablo null".
 */

export interface PersonNameFields {
  first_name?: string | null;
  last_name?: string | null;
  /** Sales/users carry their email directly. */
  email?: string | null;
  /** Contacts carry a list of emails. */
  email_jsonb?: ReadonlyArray<{ email?: string | null }> | null;
}

/** Stringified missing values that sometimes reach the database via imports. */
const MISSING_VALUE = /^(null|undefined)$/i;

/** One name part, trimmed with inner whitespace collapsed; "" when missing. */
export const cleanNamePart = (value: unknown): string => {
  if (typeof value !== "string") return "";
  const cleaned = value.trim().replace(/\s+/g, " ");
  return MISSING_VALUE.test(cleaned) ? "" : cleaned;
};

const nameParts = (person?: PersonNameFields | null): string[] =>
  [cleanNamePart(person?.first_name), cleanNamePart(person?.last_name)].filter(
    Boolean,
  );

/** "First Last", "First" or "Last"; `fallback` when both parts are missing. */
export const formatFullName = (
  person?: PersonNameFields | null,
  fallback = "",
): string => nameParts(person).join(" ") || fallback;

/** The person's first non-empty email (sales `email`, then contact emails). */
export const primaryEmail = (person?: PersonNameFields | null): string => {
  const direct = cleanNamePart(person?.email);
  if (direct) return direct;
  for (const entry of person?.email_jsonb ?? []) {
    const email = cleanNamePart(entry?.email);
    if (email) return email;
  }
  return "";
};

/** Full name, else the primary email, else `fallback` (e.g. "Sin nombre"). */
export const formatPersonName = (
  person?: PersonNameFields | null,
  fallback = "",
): string => formatFullName(person) || primaryEmail(person) || fallback;

/** First user-perceived character, so accents and emoji stay whole. */
const firstCharacter = (value: string): string => Array.from(value)[0] ?? "";

/**
 * Avatar initials: first letter of the first and last names ("Pablo" → "P",
 * "Ángel Núñez" → "ÁN"); `fallback` when there is no name at all.
 */
export const getNameInitials = (
  person?: PersonNameFields | null,
  fallback = "",
): string =>
  nameParts(person).map(firstCharacter).join("").toLocaleUpperCase() ||
  fallback;
